<template>
  <SbLoadingPromiseWrapper size="xtsm">
    <div class="row cancel cancelTop">
      <a @click="closeDialog" id="btn-layout-wrapper-cancel" class="modal__close cancel"><span
          class="screen-reader-text">Close</span></a>
    </div>
    <h4 class="mb-30 text-xs-center">{{ editShipBobNetworkPlan ? $t('EditPricingPlan') : $t('EditMerchantPlan') }}</h4>
    <div class="col">
      <div class="sub-panel mb-30">
        <div class="row mb-5">
          <div class="col left sub-panel-title">
            {{ $t('Country') }}
          </div>
        </div>
        <div class="row">
          <div class="col">
            <p :class="{'mb-0': editShipBobNetworkPlan}">{{ selectedCountry ? selectedCountry.Name : '' }}</p>
          </div>
        </div>
        <div v-if="!editShipBobNetworkPlan">
          <div class="row mb-5">
            <div class="col left sub-panel-title">
              {{ $t('NetworkPricing') }}
            </div>
          </div>
          <div class="row">
            <div class="col">
              <p>{{ selectedMerchantPlusPricingRegionByCountry ? selectedMerchantPlusPricingRegionByCountry.Name : '' }}</p>
            </div>
          </div>
          <div class="row mb-5">
            <div class="col left sub-panel-title">
              {{ $t('MerchantLocations') }}
            </div>
          </div>
          <div class="row mb-5">
            <div class="col">
              <p>{{ GetMerchantPlusFCLocationsText }}</p>
            </div>
          </div>
          <sb-panel variation="minimal">
            <div class="icon-wrapper icon-wrapper--center sb-icon-alert-circle"></div>
            <p style="font-weight: 400;"> {{ $t('IPSTicketText') }}
              <a :href='ipsTicketUrl' target="_blank">Submit IPS Ticket</a>
            </p>
          </sb-panel>
        </div>
      </div>
      <div class="row mb-5">
        <div class="col left">
          {{ $t('DomesticPricingPlan') }}
        </div>
      </div>
      <div class="row mb-30">
        <div class="col">
          <sb-dropdown-search v-model="selectedDomesticPricingPlan" :options="assignableDomesticPlans" :minSearchTermLength="0"
            @search="handleDomesticFilterPlans" :showSelectedOptions="true">
            <sb-selected-dropdown-search-option slot="selection" slot-scope="{ selection }"
              :value="mapPlanSelection(selection)" :title="mapPlanSelection(selection)"
              class="pricing-selected-dropdown"></sb-selected-dropdown-search-option>
            <sb-dropdown-option slot-scope="{ option }">{{ option.name }}</sb-dropdown-option>
          </sb-dropdown-search>
        </div>
      </div>
      <div class="row mb-5">
        <div class="col left">
          {{ $t('InternationalPricingPlan') }}
        </div>
      </div>
      <div class="row mb-30">
        <div class="col">
          <sb-dropdown-search v-model="selectedInternationalPricingPlan" :options="assignableInternationalPlans"
            :minSearchTermLength="0" @search="handleInternationalFilterPlans" :showSelectedOptions="true">
            <sb-selected-dropdown-search-option slot="selection" slot-scope="{ selection }"
              :value="mapPlanSelection(selection)" :title="mapPlanSelection(selection)"
              class="pricing-selected-dropdown"></sb-selected-dropdown-search-option>
            <sb-dropdown-option slot-scope="{ option }">{{ option.name }}</sb-dropdown-option>
          </sb-dropdown-search>
        </div>
      </div>
      <div class="mb-15 sub-panel" style="background-color: white;">
        <sb-panel variation="minimal">
          <div class="icon-wrapper icon-wrapper--center sb-icon-alert-circle"></div>
          <p style="font-weight: 400;">{{ $t('AnalyticsTicketsText') }}
            <a :href='analyticsTicketUrl' target="_blank">Submit Analytics Ticket</a>
          </p>
        </sb-panel>
      </div>
      <div class="row center-button">
        <sb-button
          v-t="saveButtonTextKey"
          variation="primary"
          class="save-btn"
          @click.prevent="handlePlanSave"
        >
        </sb-button>
      </div>
      <div class="row center-button">
        <a @click="closeDialog">{{ $t('Cancel') }}</a>
      </div>
    </div>
  </SbLoadingPromiseWrapper>
</template>

<script>
import toastr from 'toastr'
import ShipbobAppSettings from '@/appSettings/appSetting'

export default {
  name: 'EditPricingPlan',
  data: () => ({
    analyticsTicketUrl: ShipbobAppSettings.analyticsTicketsUrl,
    ipsTicketUrl: ShipbobAppSettings.ipsTicketUrl,
    domesticFilteredPlans: [],
    internationalFilteredPlans: [],
    plans: [],
    saving: false,
  }),
  computed: {
    assignableDomesticPlans() {
      this.plans = this.allAssignablePlans
      return this.domesticFilteredPlans.length === 0 ? this.plans : this.domesticFilteredPlans
    },
    assignableInternationalPlans() {
      this.plans = this.allAssignablePlans
      return this.internationalFilteredPlans.length === 0 ? this.plans : this.internationalFilteredPlans
    },
    allAssignablePlans: {
      get() {
        let allPlans = this.$shipBobStore.getters['admin/allAssignablePlans']

        let domesticPlanIndex = allPlans.findIndex(plan => plan.planId === this.selectedDomesticPricingPlan.planId)
        if(domesticPlanIndex === -1) allPlans.push(this.selectedDomesticPricingPlan)

        let internationalPlanIndex = allPlans.findIndex(plan => plan.planId === this.selectedInternationalPricingPlan.planId)
        if(internationalPlanIndex === -1) allPlans.push(this.selectedInternationalPricingPlan)

        return allPlans.filter(plan => plan.countryISO === this.selectedCountry.Id) // country specific and already assigned plans
      }
    },
    selectedMerchantPlusPricingRegionByCountry: {
      get() {
        return this.$shipBobStore.getters['admin/selectedMerchantPlusPricingRegionByCountry']
      }
    },
    GetMerchantPlusFCLocationsText() {
      let merchantPlusFCDetailsWithSelectedRegion = this.$shipBobStore.getters['admin/selectedMerchantPlusFCsWithSelectedRegion']
      if (merchantPlusFCDetailsWithSelectedRegion) {
        return merchantPlusFCDetailsWithSelectedRegion.join(', ')
      }
      return ''
    },
    selectedCountry() {
      return this.$shipBobStore.getters['admin/selectedCountry']
    },
    editShipBobNetworkPlan: {
      get() {
        return this.$shipBobStore.getters['admin/editShipBobNetworkPlan']
      }
    },
    selectedDomesticPricingPlan:
    {
      get() {
        return this.$shipBobStore.getters['admin/selectedDomesticPricingPlan']
      },
      set(domesticPricingPlan) {
        this.$shipBobStore.dispatch('admin/setSelectedDomesticAssignablePlan', domesticPricingPlan)
      }
    },
    selectedInternationalPricingPlan:
    {
      get() {
        return this.$shipBobStore.getters['admin/selectedInternationalPricingPlan']
      },
      set(internationalPricingPlan) {
        this.$shipBobStore.dispatch('admin/setSelectedInternationalAssignablePlan', internationalPricingPlan)
      }
    },
    saveButtonTextKey() {
      return this.saving ? "Saving" : "Save";
    },
  },
  methods: {
    async handlePlanSave() {
      if (this.saving) return
      this.saving = true
      let domesticPlan = this.selectedDomesticPricingPlan
      let internationalPlan = this.selectedInternationalPricingPlan
      let selectedRegionId = this.selectedMerchantPlusPricingRegionByCountry
      let selectedPricingGroupId = this.editShipBobNetworkPlan ? 1 : 2
      if (domesticPlan != null && internationalPlan != null && selectedRegionId != null) {
        let payload = { DomesticPlanId: domesticPlan.planId, InternationalPlanId: internationalPlan.planId, PricingRegionId: selectedRegionId.Id, PricingGroupId: selectedPricingGroupId }
        let res = await this.$shipBobStore.dispatch('admin/updateUserPlanData', payload)/*refresh data in drawer */
        if (res==='') {
          toastr['success']('Plan updated Successfully')
          this.closeDialog()/*closing the drawer on saving */
          this.$shipBobStore.dispatch("admin/getUserPricingPlans");
        }
      }
      else {
        toastr['error'](this.$t('Please Select Network Pricing, Domestic Pricing Plan and International Pricing Plan'))
      }
      this.saving = false
    },
    closeDialog() {
      this.$parent.handleClose()
    },
    mapPlanSelection(selection) {
      return selection ? selection.name : 'Select Pricing Plan'
    },
    handleDomesticFilterPlans({ searchTerm }) {
      this.domesticFilteredPlans = this.plans.filter(domesticPlan => {
        let unaccentedPlan = domesticPlan.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        let unaccentedSearchTerm = searchTerm.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        return unaccentedPlan.indexOf(unaccentedSearchTerm) !== -1
      })
    },
    handleInternationalFilterPlans({ searchTerm }) {
      this.internationalFilteredPlans = this.plans.filter(internationalPlan => {
        let unaccentedPlan = internationalPlan.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        let unaccentedSearchTerm = searchTerm.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        return unaccentedPlan.indexOf(unaccentedSearchTerm) !== -1
      })
    }
  },
  i18n: {
    messages: {
      'en-US': {
        Country: 'Country:',
        DomesticPricingPlan: 'Domestic Pricing Plan',
        InternationalPricingPlan: 'International Pricing Plan',
        EditPricingPlan: 'Edit ShipBob Network Pricing Plan',
        EditMerchantPlan: 'Edit Merchant+ Pricing Plans',
        NetworkPricing: 'Network Pricing:',
        MerchantLocations: 'Merchant+ Locations:',
        IPSTicketText: "Add pricing plan for individual location?",
        AnalyticsTicketsText: "Looking for a different Pricing Plan?",
        SavePlan: 'Save',
        Cancel: 'Cancel'
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import './admin-plan-panel.scss';
</style>
<style lang='scss'>
.sb-drawer__quit {
  display: none;
}

.cancelTop {
  flex-direction: row-reverse;
  font-family: proxima-nova;
}

.cancel {
  color: #9ea1a7;
}

.cancel {
  a {
    position: relative;
    bottom: 45px;
    right: 0px;
    width: 45px;
    height: 45px;

    &:before,
    &:after {
      content: ' ';
      display: block;
      position: absolute;
      top: 0px;
      right: 23px;
      width: 1px;
      height: 45px;
      background: black;
      transform: rotate(-45deg);
    }

    &:after {
      transform: rotate(45deg);
    }

    .screen-reader-text {
      display: none;
    }
  }
}
</style>